import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import BedCategorySchema from "../../../joi/master/bed-category.schema";
import {
  addBedCategoryApi,
  updateBedCategoryApi,
} from "../../../services/bed-category.service";
import { getWardApi } from "../../../services/ward.service";
import { useGetApi } from "../../../hooks/useGetApi";

export default function BedCategoryModal({
  open,
  onClose,
  refetch,
  selectedRow = null,
}) {
  const initialState = {
    name: "",
    priority: 0,
    ward: null,
    active: true,
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevValue) => {
      return {
        ...prevValue,
        [name]:
          type === "checkbox"
            ? checked
            : name === "priority"
            ? Number(value)
            : value,
      };
    });
  };
  const { dataList: wardsList } = useGetApi({
    apiFunction: getWardApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "wards",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    const { error } = BedCategorySchema.validate(formData);

    if (error) {
      error.details.forEach((err) => toast.error(err.message));
      return;
    }

    setIsLoading(true);
    if (selectedRow?._id) {
      response = await updateBedCategoryApi({
        ...formData,
        ward: formData?.ward?._id,
      });
    } else {
      response = await addBedCategoryApi({
        ...formData,
        ward: formData?.ward?._id,
      });
    }
    setIsLoading(false);

    if (response?.code === 200) {
      const raw = response?.message;
      const looksLikeHttpCode =
        raw != null && /^\d{1,3}$/.test(String(raw).trim());
      const friendly = selectedRow?._id
        ? "Bed Category updated successfully."
        : "Bed Category created successfully.";
      toast.success(
        raw != null && String(raw).trim() !== "" && !looksLikeHttpCode
          ? String(raw).trim()
          : friendly
      );
      setFormData(initialState);
      onClose();
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  // Sync selectedRow data when modal opens
  useEffect(() => {
    if (selectedRow?._id) {
      setFormData({
        ...initialState,
        _id: selectedRow._id,
        name: selectedRow?.name || "",
        ward: selectedRow?.ward || "",
        priority: selectedRow?.priority ?? 0,
        active: selectedRow?.active ?? true,
      });
    } else {
      setFormData(initialState);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        {/* Title */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "20px",
          }}
        >
          <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
            {selectedRow?._id ? `Edit Bed Category` : `Add Bed Category`}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* form */}
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Bed Category"
                name="name"
                required
                fullWidth
                value={formData?.name || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Priority"
                name="priority"
                type="number"
                required
                fullWidth
                value={formData?.priority || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={wardsList || []}
                getOptionLabel={(option) => option?.wardName || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ward"
                    name="ward"
                    fullWidth
                    required
                  />
                )}
                value={formData?.ward || null}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "ward", value: newValue },
                  })
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="active"
                    checked={formData?.active}
                    onChange={handleChange}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : selectedRow?._id ? (
                "Update"
              ) : (
                `Submit`
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
